import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { getFirstCalendar, OrderBuilderState } from '../../../reducers';
import { Calendar } from '@cemex-core/cmx-api/models/calendars/calendar';
import { Store } from '@ngrx/store';
import { ImLoadSize, LoadSize } from '@cemex-core/cmx-api';
import { UntypedFormGroup } from '@angular/forms';
import { SpecFieldNames } from '../../specification/constants/specification-field-names.enum';
import { SelectOptionI } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-select/models/select-option-i';
import { CxAlertService } from 'app/shared/cx-alert/cx-alert.service';
import { TranslateService } from '@ngx-translate/core';

interface LoadSizeLimits {
  minLoadSize: number;
  maxLoadSize: number;
}

/**
 * Retrieves the load size limits (minimum and maximum) from the store.
 * @param store - The NgRx store containing the application state.
 */
export function getLoadSizeLimits(store: Store<OrderBuilderState>): Observable<LoadSizeLimits> {
  return store.select(getFirstCalendar).pipe(
    filter((calendar) => !!calendar),
    map((state: Calendar) => {
      return {
        minLoadSize: state.minLoadSize,
        maxLoadSize: state.maxLoadSize || state.defTruckVolume,
      };
    }),
  );
}

/**
 * Filters load sizes to return only those that fall within the specified min and max load size limits.
 * @param loadSizes - Array of load sizes to be filtered.
 * @param minLoadSize - Minimum acceptable load size.
 * @param maxLoadSize - Maximum acceptable load size.
 * @param isArkikEnabled - Boolean indicating if the Arkik experience is enabled.
 */
export function filterValidLoadSizes(loadSizes: ImLoadSize[], minLoadSize: number, maxLoadSize: number, isArkikEnabled: boolean): ImLoadSize[] {
  if (isArkikEnabled) {
    return loadSizes.filter((loadSize) => loadSize.loadSizeQuantity <= maxLoadSize);
  }
  return loadSizes.filter(
    (loadSize) => loadSize.loadSizeQuantity >= minLoadSize && loadSize.loadSizeQuantity <= maxLoadSize,
  );
}

/**
 * Handles the selection of a load size in a form for modify orders.
 * It checks if the recipe load size is within valid limits.
 * @param formGroup - The form group to update.
 * @param validLoadSizes - Array of valid load sizes to choose from.
 * @param recipeLoadSize - Load Size of current order.
 * @param loadSizes - Array of load sizes from API call.
 * @param loadSizeOutOfCatalogAndRange - load size out of catalog should be discard when load size is not found
 */
export function handleLoadSizeSelectionForModify(
  formGroup: UntypedFormGroup,
  validLoadSizes: ImLoadSize[],
  recipeLoadSize: string,
  loadSizes: ImLoadSize[],
  loadSizeOutOfCatalogAndRange: LoadSize,
) {
  const matchingLoadSize = loadSizes.find((loadSize) => loadSize.loadSizeCode === recipeLoadSize);
  const matchingValidLoadSize = validLoadSizes.find((loadSize) => loadSize.loadSizeCode === recipeLoadSize);
  let selectedLoadSize: ImLoadSize;
  if (matchingLoadSize) {
    if (matchingValidLoadSize) {
      selectedLoadSize = matchingLoadSize;
    } else {
      validLoadSizes.unshift(matchingLoadSize);
      selectedLoadSize = matchingLoadSize;
    }
  } else {
    validLoadSizes = !!loadSizeOutOfCatalogAndRange
      ? validLoadSizes.filter((loadSize) => loadSize.loadSizeCode !== loadSizeOutOfCatalogAndRange.loadSizeCode)
      : validLoadSizes;
    selectedLoadSize = validLoadSizes.reduce(
      (max, current) => (current.loadSizeQuantity > max.loadSizeQuantity ? current : max),
      validLoadSizes[0],
    );
  }
  formGroup.get(SpecFieldNames.LOAD_SIZE).setValue(selectedLoadSize.loadSizeCode, { emitEvent: false });
  formGroup.get(SpecFieldNames.LOAD_SIZE).updateValueAndValidity({ emitEvent: false });
}

/**
 * Handles the selection of a load size in a form for repeat orders.
 * It checks if the recipe load size is within valid limits, and triggers alerts if necessary.
 * @param formGroup - The form group to update.
 * @param validLoadSizes - Array of valid load sizes.
 * @param currentLoadSize - Current Load Size of current specification.
 */
export function handleLoadSizeSelectionForRepeat(
  formGroup: UntypedFormGroup,
  validLoadSizes: ImLoadSize[],
  recipeLoadSize: string,
  cxAlertService: CxAlertService,
  translate: TranslateService,
) {
  const matchingLoadSize = validLoadSizes.find((loadSize) => loadSize.loadSizeCode === recipeLoadSize);

  if (matchingLoadSize) {
    formGroup.get(SpecFieldNames.LOAD_SIZE).setValue(matchingLoadSize.loadSizeCode, { emitEvent: false });
  } else {
    const repeatError = 'repeatError';
    formGroup.get(SpecFieldNames.LOAD_SIZE).setValue(undefined, { emitEvent: false });

    if (!cxAlertService.isCwcAlertCreated(repeatError) && recipeLoadSize) {
      cxAlertService.createAlert(
        {
          message: translate.instant('views.order.single_catalogs.repeat_error'),
          variant: 'error',
        },
        repeatError,
      );
    }
  }
}

/**
 * Helper to return options
 * @param validLoadSizes - Array of valid load sizes.
 */
export function mapLoadSizeOptions(validLoadSizes: ImLoadSize[]): SelectOptionI[] {
  return validLoadSizes.map(({ loadSizeCode, loadSizeDesc }) => ({
    id: loadSizeCode,
    name: loadSizeDesc,
  }));
}

/**
 * Creates Select options from a list of load sizes.
 * @param loadSizes - Array of load sizes.
 */
export function createSelectOptions(loadSizes: ImLoadSize[]): SelectOptionI[] {
  return loadSizes.map((loadSize) => ({
    id: loadSize.loadSizeCode,
    name: loadSize.loadSizeDesc,
  }));
}
